import React, { useState, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

export interface TabBarTab {
  key?: string;
  label: string;
  value?: string;
  badge?: number;
  count?: number;
  icon?: React.ReactNode;
  /** Active-state accent override — 'danger' renders the active tab in the danger token (e.g. a Danger Zone tab). */
  tone?: 'danger';
}

/** @deprecated Use TabBarTab instead */
export type Tab = TabBarTab;

export interface TabBarProps {
  tabs: TabBarTab[];
  activeTab: string;
  onChange: (key: string) => void;
  variant?: 'default' | 'underline' | 'pill' | 'segmented';
  size?: 'sm' | 'md';
  className?: string;
}

const variantStyles = {
  default: {
    container: 'gap-0',
    active: 'border-b-2 border-accent text-accent',
    inactive: 'border-b-2 border-transparent text-text-secondary hover:text-text-primary',
  },
  underline: {
    container: 'gap-0',
    active: 'border-b-2 border-accent text-accent',
    inactive: 'border-b-2 border-transparent text-text-secondary hover:text-text-primary',
  },
  pill: {
    container: 'gap-1',
    active: 'bg-surface text-text-primary rounded-lg',
    inactive: 'bg-transparent text-text-secondary hover:text-text-primary rounded-lg',
  },
  segmented: {
    container: 'bg-bg-secondary p-1 rounded-lg gap-1',
    active: 'bg-transparent text-accent rounded-none border-b-2 border-accent',
    inactive: 'bg-transparent text-text-secondary hover:text-text-primary rounded-none border-b-2 border-transparent',
  },
};

// Active-state overrides for tone='danger' — same shape per variant as `active` above.
const dangerActiveStyles: Record<NonNullable<TabBarProps['variant']>, string> = {
  default: 'border-b-2 border-danger text-danger',
  underline: 'border-b-2 border-danger text-danger',
  pill: 'bg-surface text-danger rounded-lg',
  segmented: 'bg-transparent text-danger rounded-none border-b-2 border-danger',
};

const sizeStyles = { sm: 'px-3 py-1.5 text-sm', md: 'px-4 py-2 text-sm' };

export function TabBar({ tabs, activeTab, onChange, variant = 'underline', size = 'md', className }: TabBarProps) {
  const [focusedIndex, setFocusedIndex] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const idx = tabs.findIndex(t => (t.key ?? t.value) === activeTab);
    if (idx !== -1) setFocusedIndex(idx);
  }, [activeTab, tabs]);

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = (index + 1) % tabs.length;
      setFocusedIndex(next);
      tabRefs.current[next]?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prev = (index - 1 + tabs.length) % tabs.length;
      setFocusedIndex(prev);
      tabRefs.current[prev]?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onChange(tabs[index].key || tabs[index].value || '');
    }
  };

  const styles = variantStyles[variant];

  return (
    <div className={twMerge('flex overflow-x-auto', styles.container, className)} role="tablist">
      {tabs.map((tab, index) => (
        <button
          key={tab.key ?? tab.value}
          ref={el => (tabRefs.current[index] = el)}
          className={twMerge(
            sizeStyles[size],
            'font-medium cursor-pointer transition-colors whitespace-nowrap border-none min-h-touch',
            'focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2',
            activeTab === (tab.key || tab.value)
              ? (tab.tone === 'danger' ? dangerActiveStyles[variant] : styles.active)
              : styles.inactive
          )}
          role="tab"
          aria-selected={activeTab === (tab.key || tab.value)}
          onClick={() => onChange(tab.key || tab.value || '')}
          onKeyDown={e => handleKeyDown(e, index)}
          tabIndex={focusedIndex === index ? 0 : -1}
        >
          {tab.icon && <span className="mr-1.5">{tab.icon}</span>}
          {tab.label}
          {((tab.badge !== undefined && tab.badge > 0) || (tab.count !== undefined && tab.count > 0)) && (
            <span className="ml-1.5 inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1 text-xs font-bold rounded-full bg-accent text-white">
              {(tab.badge || tab.count || 0) > 99 ? '99+' : (tab.badge || tab.count)}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
